import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { OutwriteApi } from '../services/OutwriteService';
import OutwriteLlmModelSelect from './OutwriteLlmModelSelect';

/**
 * Outwrite SETUP: LLM models + rubric severity catalog (DB).
 */
export default function OutwriteSetup() {
  const [models, setModels] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [id, setId] = useState('');
  const [mode, setMode] = useState('');
  const [label, setLabel] = useState('');
  const [severityValue, setSeverityValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      OutwriteApi.listLlmModels().catch(() => []),
      OutwriteApi.listRubricsSeverities().catch(() => []),
    ])
      .then(([modelList, severityList]) => {
        setModels(Array.isArray(modelList) ? modelList : []);
        setSeverities(Array.isArray(severityList) ? severityList : []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload, reloadToken]);

  const handleAdd = () => {
    const nextId = id.trim();
    const nextMode = mode.trim();
    if (!nextId || !nextMode || saving) return;
    setSaving(true);
    OutwriteApi.createLlmModel(nextId, nextMode, label.trim() || undefined)
      .then(() => {
        setId('');
        setMode('');
        setLabel('');
        setReloadToken((n) => n + 1);
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const handleDelete = (modelId, modelMode) => {
    if (saving) return;
    setSaving(true);
    OutwriteApi.deleteLlmModel(modelId, modelMode)
      .then(() => setReloadToken((n) => n + 1))
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const handleAddSeverity = () => {
    const next = severityValue.trim();
    if (!next || saving) return;
    setSaving(true);
    OutwriteApi.createRubricsSeverity(next)
      .then(() => {
        setSeverityValue('');
        setReloadToken((n) => n + 1);
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const handleDeleteSeverity = (value) => {
    if (saving) return;
    setSaving(true);
    OutwriteApi.deleteRubricsSeverity(value)
      .then(() => setReloadToken((n) => n + 1))
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" component="h1">
            Setup
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Available LLM models (stored in DB). Active selection applies process-wide.
          </Typography>
        </Box>
        <OutwriteLlmModelSelect disabled={saving} key={reloadToken} />
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2 }}
        alignItems={{ sm: 'flex-start' }}
      >
        <TextField
          label="Model id"
          size="small"
          value={id}
          onChange={(e) => setId(e.target.value)}
          disabled={saving}
          sx={{ flex: 2 }}
        />
        <TextField
          label="Mode"
          size="small"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          disabled={saving}
          sx={{ flex: 1 }}
        />
        <TextField
          label="Label (optional)"
          size="small"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={saving}
          sx={{ flex: 2 }}
        />
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={saving || !id.trim() || !mode.trim()}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add
        </Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Model id</TableCell>
            <TableCell>Mode</TableCell>
            <TableCell>Label</TableCell>
            <TableCell align="right" width={72} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading &&
            models.map((m) => (
              <TableRow key={`${m.id}::${m.mode}`}>
                <TableCell>{m.id}</TableCell>
                <TableCell>{m.mode}</TableCell>
                <TableCell>{m.label}</TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label={`Delete ${m.id}/${m.mode}`}
                    size="small"
                    disabled={saving}
                    onClick={() => handleDelete(m.id, m.mode)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          {!loading && models.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary">
                  Catalog empty — runtime falls back to env/client default until you add models.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Box sx={{ mt: 5, mb: 2 }}>
        <Typography variant="h6" component="h2">
          Rubric severity
        </Typography>
          <Typography variant="body2" color="text.secondary">
          Values for the severity dropdown on Tasks.
        </Typography>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2 }}
        alignItems={{ sm: 'flex-start' }}
      >
        <TextField
          label="Severity value"
          size="small"
          value={severityValue}
          onChange={(e) => setSeverityValue(e.target.value)}
          disabled={saving}
          sx={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddSeverity();
          }}
        />
        <Button
          variant="contained"
          onClick={handleAddSeverity}
          disabled={saving || !severityValue.trim()}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add
        </Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Value</TableCell>
            <TableCell align="right" width={72} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading &&
            severities.map((s) => (
              <TableRow key={s.value}>
                <TableCell>{s.value}</TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label={`Delete severity ${s.value}`}
                    size="small"
                    disabled={saving}
                    onClick={() => handleDeleteSeverity(s.value)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          {!loading && severities.length === 0 && (
            <TableRow>
              <TableCell colSpan={2}>
                <Typography variant="body2" color="text.secondary">
                  No severity values yet — add minor / substantial / critical or
                  custom labels.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
